/**
 * A right-hand panel for one row's detail.
 *
 * This replaces the inline expanded table row. The reason is not fashion: a
 * rationale is several sentences, an evidence list is a handful of links and a
 * ligand starting point is a small table of its own, and all of that was being laid
 * out inside a `<td colSpan={13}>`. The colSpan is the tell — it is bookkeeping that
 * every column change has to chase, and it silently goes wrong rather than failing.
 * Prose that needs a measure of its own does not belong in a cell.
 *
 * It also fixes a real reading problem. An expanded row pushes the rows below it off
 * the screen, so comparing a target with the one under it means collapsing the thing
 * you are reading. A drawer leaves the ranking in place beside it.
 *
 * ── Accessibility, which is the whole reason a hand-rolled panel is acceptable ──
 *
 * `ConfirmDialog` established the pattern in this codebase and this follows it
 * deliberately rather than reaching for a dialog library: `role="dialog"`,
 * `aria-modal`, a labelled title, Escape to close, focus moved in on open and
 * *restored* to whatever opened it on close. Skipping those is what makes a
 * hand-rolled panel a mistake; the library is not.
 *
 * One deliberate difference from `ConfirmDialog`: this traps Tab inside the panel.
 * A modal dialog with two buttons does not really need it, but a drawer holds links,
 * a report button and a scrollable region, and tabbing out of an open overlay into
 * the table behind it lands focus on rows the reader cannot see.
 *
 * ── Why it portals ──
 *
 * `fixed inset-0` is only relative to the viewport while no ancestor establishes a
 * containing block, and in this app several do. Every animation in
 * `tailwind.config.js` is declared with fill-mode `both`, so `animate-rise` leaves
 * `transform: translateY(0)` applied *permanently* — and an element whose transform
 * is anything other than `none` becomes the containing block for its `fixed`
 * descendants, and a stacking context besides. A drawer inside an `animate-rise`
 * card therefore covers the card instead of the page, at full opacity, looking
 * deliberate. `tsc`, `eslint` and `test_ui_theme.py` are all blind to it, and it
 * cost one debugging cycle before this component portalled.
 *
 * So the overlay renders into `document.body`, which makes the fault structurally
 * unreachable rather than a rule the next editor has to know. No animation library
 * either — the panel uses the `slide-in` keyframe from `tailwind.config.js`, which
 * the global `prefers-reduced-motion` rule in `index.css` already turns off.
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'
import { cn } from '../../lib/utils'

/** Everything focusable, minus anything explicitly taken out of the tab order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface Props {
  /** Rendered as the panel's accessible name, so it must say which row this is. */
  title: string
  /**
   * The line under the title: an accession, a mechanism, a target key. Optional
   * because not every row has one, and a blank line is worse than none.
   */
  subtitle?: ReactNode
  /**
   * A short label above the title naming what *kind* of record this is — "Ranked
   * target", "Board entry". A run's ranking and the cumulative board are different
   * levels of record and a drawer that does not say which one it came from invites
   * reading a cross-run consolidation as one run's result.
   */
  eyebrow?: string
  children: ReactNode
  /** Rendered in a footer that does not scroll away — where an action belongs. */
  footer?: ReactNode
  onClose: () => void
  className?: string
}

export function Drawer({
  title,
  subtitle,
  eyebrow,
  children,
  footer,
  onClose,
  className,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  // Focus in on open, and back to the row on close. Without the restore, closing
  // drops focus to <body> and a keyboard reader has to tab from the top of the
  // page back to the row they were reading.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => opener?.focus?.()
  }, [])

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      // Yield the keyboard to a modal above us. `ReportButton` sits in the footer
      // and opens `ConfirmDialog`, which also portals and also listens for Escape
      // on the document — so without this guard one Escape both cancels the report
      // *and* closes the drawer behind it, losing the reader's place. Both handlers
      // are on `document`, so listener order decides, and the drawer registered
      // first: it has to stand down, not race.
      //
      // The test is ownership, not mere containment: focus legitimately sits on
      // <body> after a backdrop click, and Escape must still close the drawer then.
      const owner = (document.activeElement as HTMLElement | null)?.closest(
        '[role="dialog"]',
      )
      if (owner && owner !== panelRef.current) return

      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!nodes || nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      // Wrap at both ends. Checking `activeElement` rather than the event target
      // matters when focus is on the panel itself, which happens right after the
      // close button is removed by a re-render.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  // On the document, not the panel: focus can sit on the backdrop or move to a
  // link inside the body, and a handler bound to the panel stops working then.
  useEffect(() => {
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onKey])

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-950/60 animate-fade-in"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        // Clicks inside must not reach the backdrop handler, or selecting a
        // rationale to copy it closes the panel.
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex h-full w-full max-w-[560px] flex-col border-l border-slate-800',
          'bg-slate-900 shadow-pop animate-slide-in',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 px-5 py-4">
          <div className="min-w-0">
            {eyebrow && (
              <p className="font-display text-2xs uppercase tracking-eyebrow text-slate-500">
                {eyebrow}
              </p>
            )}
            <h2
              id="drawer-title"
              className="truncate font-display text-base font-semibold text-slate-100"
            >
              {title}
            </h2>
            {subtitle && (
              <div className="mt-0.5 text-xs text-slate-400">{subtitle}</div>
            )}
          </div>
          <Button
            ref={closeRef}
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
            className="-my-1 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* The only scrolling region. The header names the row and the footer
            holds the action, and neither should scroll away from a reader half
            way down a long rationale. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="border-t border-slate-800 px-5 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * A labelled block inside a drawer. Two levels of heading only, because a detail
 * panel that grows a heading hierarchy is a page and should be one.
 */
export function DrawerSection({
  label,
  children,
  className,
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('mb-5 last:mb-0', className)}>
      <h3 className="mb-1.5 font-display text-2xs uppercase tracking-eyebrow text-slate-500">
        {label}
      </h3>
      {children}
    </section>
  )
}
